// RechargeLockedPanel — calm "temporarily unavailable" panel rendered
// in place of PaymentReceiverCard / ParentPaymentCard when the
// CASCADE RECHARGE LOCK fires on the server.
//
// ───────────────────────────────────────────────────────────────────
// WHEN THIS RENDERS
//
// The server (depositGuards.getParentSuspension, called from
// /me/payment-config and /admin/me-agent/emergency-recharge) checks
// whether the recharger's IMMEDIATE PARENT agent is auto-suspended
// (agents.suspended_at IS NOT NULL — set by the 100% deposit-breach
// guard). When suspended:
//
//   /me/payment-config             → { parent_locked: true, ... }
//   /admin/me-agent/emergency-recharge → { parent_locked: true, parent: {...redacted...} }
//
// Both flows omit upi_id, qr_image_url, whatsapp_number, support_phone
// from the payload. The corresponding frontend wizard step swaps
// PaymentReceiverCard / ParentPaymentCard for THIS panel.
//
// ───────────────────────────────────────────────────────────────────
// WHY A SEPARATE PANEL (rather than a banner above the QR)?
//
// The whole point of the Cascade Lock is to make sure the player /
// sub-agent CAN'T send money to a defaulter. If we showed a banner
// AND the QR, a careless user might still scan the QR. Replacing the
// payment surface entirely is the safest UX.
//
// ───────────────────────────────────────────────────────────────────
// WHY THE TONE IS CALM (no red, no alarm icons)
//
// A panic-y UI ("WARNING — ACCOUNT BLOCKED") would scare the user
// into thinking THEIR account is broken. Reality is the opposite —
// only their PATH to recharge is paused; their wallet, balance, and
// withdrawals all work normally. A reassuring amber tone communicates
// "we're working on it" without alarm.
//
// ───────────────────────────────────────────────────────────────────
// PARENT MIGHT STILL APPROVE EXISTING REQUESTS
//
// A suspended agent CAN still approve in-flight pending requests
// (the platform allows that one operation). So any pending recharge
// the user submitted BEFORE the suspension can still be credited.
// We tell the user this so they don't worry about earlier payments
// being stuck.

import Icon from './Icon';
import { useT } from '../lib/i18n';

/** Which calming message to surface.
 *
 *  'cascade_lock'    — parent agent is breached/over capacity. The
 *                      cascade-lock path written for the Dynamic
 *                      Capacity feature. Copy emphasises "we are
 *                      resolving a problem with this recharge route."
 *  'not_configured'  — parent agent has not yet activated the QR
 *                      for THIS downline role (player vs sub-agent).
 *                      Copy emphasises "recharge will be available
 *                      soon — we are finalising setup." Different
 *                      cause, similar calming tone. The user
 *                      deliberately asked for a less-alarming variant
 *                      because nothing is "wrong" — setup is just
 *                      incomplete.
 */
export type LockVariant = 'cascade_lock' | 'not_configured';

export default function RechargeLockedPanel({
  /** Caller decides whether to surface a Close button. The player
   *  flow shows one (the modal stays open and Close closes it);
   *  the sub-agent inline form surfaces a Cancel via its own
   *  outer chrome and passes onClose={undefined}. */
  onClose,
  /** Default keeps the existing cascade-lock copy so every existing
   *  call site renders the same as before this prop was added. */
  variant = 'cascade_lock',
}: {
  onClose?: () => void;
  variant?: LockVariant;
}) {
  const t = useT();
  const titleKey = variant === 'not_configured'
    ? 'recharge.notConfigured.title'
    : 'recharge.locked.title';
  const bodyKey = variant === 'not_configured'
    ? 'recharge.notConfigured.body'
    : 'recharge.locked.body';

  return (
    <div className="space-y-3">
      <div className="rounded-2xl
                      bg-amber-50/80 dark:bg-amber-500/10
                      border-2 border-amber-300 dark:border-amber-400/30
                      px-5 py-6 text-center">
        {/* Soft amber circle with a clock/build icon. Deliberately
            NOT 'error' / 'warning' / 'block' — those read as
            "your account is in trouble". 'build' / 'handyman' /
            'support' communicate "someone is working on a fix". */}
        <div className="mx-auto w-14 h-14 rounded-full
                        bg-amber-100 dark:bg-amber-500/20
                        ring-2 ring-amber-200 dark:ring-amber-400/40
                        grid place-items-center mb-3">
          <Icon
            name="build"
            size={28}
            filled
            className="text-amber-700 dark:text-amber-300"
          />
        </div>
        <h3 className="font-display font-extrabold text-base
                       text-amber-900 dark:text-amber-100 mb-1.5">
          {t(titleKey)}
        </h3>
        <p className="text-[13px] leading-snug
                      text-amber-900/85 dark:text-amber-100/85
                      max-w-md mx-auto">
          {t(bodyKey)}
        </p>
        {/* Reassurance about WITHDRAWALS + EXISTING PENDING REQUESTS.
            Two separate lines because each addresses a different
            anxiety: "is my money stuck?" (existing pending) and
            "can I get my money out?" (withdrawals). */}
        <div className="mt-4 inline-flex flex-col gap-1.5 text-left
                        text-[12px] text-amber-900/90 dark:text-amber-100/85">
          <div className="flex items-start gap-2">
            <Icon name="check_circle" size={16} className="text-amber-700 dark:text-amber-300 mt-px shrink-0" filled />
            <span>{t('recharge.locked.reassure.withdrawals')}</span>
          </div>
          <div className="flex items-start gap-2">
            <Icon name="check_circle" size={16} className="text-amber-700 dark:text-amber-300 mt-px shrink-0" filled />
            <span>{t('recharge.locked.reassure.existing')}</span>
          </div>
        </div>
      </div>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="w-full h-11 rounded-xl text-[14px] font-bold
                     bg-arena-100 dark:bg-arena-700/60
                     text-arena-700 dark:text-neon-100
                     hover:bg-arena-200 dark:hover:bg-arena-700"
        >
          {t('common.close')}
        </button>
      )}
    </div>
  );
}
